import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ListLayout } from '../../global/tables/table-list-layout';
import TableLoadingRow from '../../global/tables/table-loading-row';
import DefaultButton from '../../global/default-button/default-button';
import { Paging } from '../../global/_paging/paging-container';
import { AccountGroupsVM } from '../account-groups.viewmodel';
import { IAccountGroupDTO } from '../../../api/account-groups.dao';
import { AccountGroupsListItem } from './_account-groups-list-item/account-groups-list-item';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { DESCRIPTION } from '../../../constants/common-labels.constants';

interface IProps {
    intl: IntlShape;
    vm: AccountGroupsVM;
}

const AccountGroupsList = injectIntl(
    mobxReact.observer((props: IProps) => {
        const { vm, intl } = props;
        const { accountGroupsList, showCreateGroupForm, isLoading } = vm;

        const title = intl.formatMessage({
            id: 'oe.accountGroups.pageTitle',
            defaultMessage: 'Account Groups',
            description: 'Section header',
        });

        const thead = [
            intl.formatMessage({
                id: 'oe.common.name',
                description: 'Label text',
                defaultMessage: 'Name',
            }),
            intl.formatMessage(DESCRIPTION),
        ];

        const isEmpty = !accountGroupsList?.length;

        return (
            <div className='page-container'>
                <section className='top-page-title horizontal-row horizontal-row--with-bottom-margin'>
                    <h1 className='text-ellipsis'>{title}</h1>
                    <div className='horizontal-row__align-to-row-end'>
                        <div className='buttons-block'>
                            {hasPermission(Permission.CREATE_ACCOUNT_GROUP_LIST) && (
                                <DefaultButton
                                    className='events-list__button-new'
                                    modifier='fat'
                                    text={intl.formatMessage(
                                        {
                                            id: `oe.accountGroups.newAccountGroup`,
                                            description: 'New Account button',
                                            defaultMessage: '{prefix}New Account Group',
                                        },
                                        {
                                            prefix: '+ ',
                                        }
                                    )}
                                    onClick={() => showCreateGroupForm(intl)}
                                />
                            )}
                        </div>
                    </div>
                </section>
                <section className='accounts-list fixed-list-layout'>
                    <div>
                        <ListLayout thead={thead}>
                            {!isLoading &&
                                !isEmpty &&
                                accountGroupsList.map((item: IAccountGroupDTO) => {
                                    return (
                                        <AccountGroupsListItem
                                            key={item.accountGroupId}
                                            isGroupSelected={vm.isGroupFormShown(item.accountGroupId + 'group')}
                                            handleEditGroupClick={(accountGroupId: number) => vm.editGroup(intl, accountGroupId)}
                                            {...item}
                                        />
                                    );
                                })}
                            {isLoading && <TableLoadingRow colSpan={thead.length} />}
                        </ListLayout>
                        {!isLoading && isEmpty && (
                            <div className='empty-list-message'>
                                <FormattedMessage
                                    description='Account groups list empty text'
                                    defaultMessage='There are no account groups.'
                                    id='oe.accountGroups.emptyListText'
                                />
                            </div>
                        )}
                    </div>
                </section>
                {!isLoading && <Paging pagingVM={vm.paginationVM} />}
            </div>
        );
    })
);

export default AccountGroupsList;
